<script setup lang="ts">
import type { User } from '@/types/UserData';

type Props = {
  opacity?: boolean;
};

const { opacity = false } = defineProps<Props>();
const emit = defineEmits(['check-pendency']);

interface UserTwoFactor {
  enabled: number;
  type: User['userInfo']['two_factor_type'];
}

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { isTwoFactorAuthEnabled } = useAuth();

const { isTwoFactorEnabled, userTwoFactorType, fetchUserProfile } =
  useUserData();

const isLoading = ref(false);
const isEditing = ref<boolean>(false);
const userTwoFactorAuth = ref<UserTwoFactor>({
  enabled: 0,
  type: 'email',
});

const choiceOptions = [
  { value: 0, label: t('general.no') },
  { value: 1, label: t('general.yes') },
];

const methodsOptions = [
  { value: 'email', label: t('account.security.two_factor_auth.email') },
  { value: 'sms', label: t('account.security.two_factor_auth.sms') },
];

const currentTwoFactorTypeSelected = computed<string>(() => {
  return (
    methodsOptions.find((option) => {
      return option.value === userTwoFactorAuth.value.type;
    })?.label || ''
  );
});

function toggleEdit() {
  isEditing.value = !isEditing.value;
}

async function handleSubmitForm() {
  await $containment.handle('update_data', async () => {
    await submit();
  });
}

type UpdateTwoFactorAuth = {
  two_factor_enabled: number;
  two_factor_type: string;
  kyc_id?: number;
  password_check?: string;
};

async function submit() {
  if (!isTwoFactorAuthEnabled.value || !userTwoFactorAuth.value.type) {
    return;
  }

  isLoading.value = true;

  const { ApiService } = useApiService();
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  const sendData: UpdateTwoFactorAuth = {
    two_factor_enabled: userTwoFactorAuth.value.enabled,
    two_factor_type: userTwoFactorAuth.value.type,
  };

  if ($kyc?.current?.id) {
    sendData.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    sendData.password_check = temporaryPassword.get();
  }

  await ApiService.patch('/bff/users/self-two-factor', sendData)
    .then(async () => {
      const message = userTwoFactorAuth.value.enabled
        ? t('account.security.two_factor_auth.activated', {
            type: currentTwoFactorTypeSelected.value,
          })
        : t('account.security.two_factor_auth.unactivated');

      openToaster({
        message,
        type: 'success',
      });

      await fetchUserProfile({ force: true });

      isEditing.value = false;
    })
    .catch((err) => {
      const errorData = err?.data?.message;

      const errorMessage =
        errorData || t('account.user_data.errors.update_error');

      openToaster({
        message: errorMessage,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
      emit('check-pendency');
      temporaryPassword.clear();
    });
}

function start() {
  userTwoFactorAuth.value.enabled = isTwoFactorEnabled.value ? 1 : 0;
  userTwoFactorAuth.value.type = userTwoFactorType.value;
}

const hasPendency = computed(() => {
  return isTwoFactorAuthEnabled.value && isTwoFactorEnabled.value;
});

defineExpose({
  name: 'twoFactorAuth',
  isEditing,
  hasPendency,
  toggleEdit,
});

onMounted(() => {
  start();
});

onActivated(() => {
  start();
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />
    <div :class="[$style.toggleBox, { [$style.opacity]: opacity }]">
      <div :class="$style.header">
        <p :class="$style.title">
          {{ $t('account.security.two_factor_auth.title') }}
          <NuxtIcon
            v-tooltip.bottom.left="$t('account.security.two_factor_auth.info')"
            name="info"
          />
        </p>

        <button
          v-if="!isTwoFactorAuthEnabled"
          :class="$style.soon"
        >
          {{ $t('general.soon') }}
        </button>

        <button
          v-else
          :class="$style.edit"
          @click="toggleEdit"
        >
          {{ isEditing ? $t('general.cancel') : $t('general.edit') }}
        </button>
      </div>
      <div
        v-if="!isEditing"
        :class="$style.viewContent"
      >
        <p :class="$style.text">
          <template v-if="!isTwoFactorEnabled || !isTwoFactorAuthEnabled">
            {{ $t('account.security.two_factor_auth.unactivated') }}
          </template>

          <template v-else>
            <span
              v-html="
                $t('account.security.two_factor_auth.activated', {
                  type: currentTwoFactorTypeSelected,
                })
              "
            />
          </template>
        </p>
      </div>
      <div
        v-else-if="isTwoFactorAuthEnabled"
        :class="$style.viewContent"
      >
        <p
          :class="$style.text"
          v-html="$t('account.security.two_factor_auth.text')"
        />

        <div :class="$style.groupRadio">
          <label>
            {{ $t('account.security.two_factor_auth.activate') }}
          </label>
          <BaseFormRadio
            v-model="userTwoFactorAuth.enabled"
            :options="choiceOptions"
          />
        </div>

        <div
          v-if="userTwoFactorAuth.enabled"
          :class="$style.groupFields"
        >
          <div :class="$style.groupInput">
            <p :class="$style.tip">
              {{ $t('account.security.two_factor_auth.select_method') }}
            </p>

            <BaseFormSelectField
              id="user_two_auth_method"
              v-model="userTwoFactorAuth.type"
              :disabled="false"
              :initial-value="userTwoFactorAuth.type"
              name="user_two_auth_method"
              :options="methodsOptions"
            />
          </div>
        </div>

        <button
          :class="$style.btnSubmit"
          :disabled="false"
          @click="handleSubmitForm"
        >
          <NuxtIcon name="check" />
          {{ $t('account.save') }}
        </button>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.toggleBox {
  @apply flex flex-col gap-3 p-1 md:p-0 px-1 md:px-2;

  &.opacity {
    @apply opacity-20 pointer-events-none select-none;
  }

  .header {
    @apply flex items-start justify-between;

    .title {
      @apply text-xs sm:text-sm font-semibold flex items-center gap-2;
    }

    .soon {
      @apply text-xs font-bold uppercase opacity-30 px-1 cursor-wait whitespace-nowrap;
    }

    .edit {
      @apply text-xs font-bold uppercase opacity-60 px-1;
    }
  }

  .viewContent {
    @apply text-base text-texts/80;

    .text {
      @apply text-xs mb-2;
    }

    .groupRadio {
      @apply flex items-center gap-5 font-semibold text-xs md:text-sm text-texts;
    }

    .groupFields {
      @apply flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-5 mt-5;

      .tip {
        @apply text-sm;
      }

      .groupInput {
        @apply w-full min-w-40 md:max-w-52;
      }
    }

    .btnSubmit {
      @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 mt-5 disabled:opacity-40 disabled:cursor-not-allowed;
    }
  }
}
</style>
